import { unwrap } from '@/lib/apiEnvelope'
import { DOWNLOAD_TIMEOUT_MS, getFileNameFromDisposition, parseBlobError } from '@/lib/download'
import { httpClient } from '@/lib/httpClient'
import { UPLOAD_TIMEOUT_MS } from '@/lib/upload'
import type { ApiResponse, Paginated } from '@/types/api'

import type {
  CalendarioArquivoPayload,
  EtapaUpdatePayload,
  FotoUploadPayload,
  RelatorioFilters,
  SugestaoFilters,
  Visita,
  VisitaCreatePayload,
  VisitaDetalhe,
  VisitaExclusaoPayload,
  VisitaHistorico,
  VisitaListFilters,
  VisitaSugestao,
} from '../types/visita'

type MinhasVisitasFilters = Pick<
  VisitaListFilters,
  'dataInicio' | 'dataFim' | 'status' | 'page' | 'pageSize'
>
type RevisaoFilters = Omit<VisitaListFilters, 'status'>

/** Teto da exportação de calendários (até 30 gerados em sequência pela API). */
const CALENDARIO_TIMEOUT_MAX_MS = 5 * 60_000

const getDetalhe = async (url: string) =>
  unwrap(await httpClient.get<ApiResponse<VisitaDetalhe>>(url))
const postDetalhe = async (url: string, body?: unknown) =>
  unwrap(await httpClient.post<ApiResponse<VisitaDetalhe>>(url, body))

/** Planejamento (planejador). */
export const visitasApi = {
  list: async (filters: VisitaListFilters): Promise<Paginated<Visita>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<Visita>>>('/operacional/visitas', {
        params: filters,
      }),
    ),
  sugestoes: async (filters: SugestaoFilters): Promise<VisitaSugestao[]> =>
    unwrap(
      await httpClient.get<ApiResponse<VisitaSugestao[]>>('/operacional/visitas/sugestoes', {
        params: filters,
      }),
    ),
  /**
   * Calendários do mês fora do envelope: 1 item → PDF/PNG; vários → `.zip`. Nome do arquivo
   * no Content-Disposition. A API gera um por vez, então o timeout cresce com a quantidade.
   */
  calendarioArquivo: async (
    payload: CalendarioArquivoPayload,
  ): Promise<{ blob: Blob; fileName: string }> => {
    const quantidade = payload.calendarios.length
    const response = await httpClient
      .post<Blob>('/operacional/visitas/calendario', payload, {
        responseType: 'blob',
        timeout: Math.min(DOWNLOAD_TIMEOUT_MS * quantidade, CALENDARIO_TIMEOUT_MAX_MS),
      })
      .catch(parseBlobError)
    const disposition = response.headers['content-disposition'] as string | undefined
    const sufixo = `${payload.ano}_${String(payload.mes).padStart(2, '0')}`
    const fallback =
      quantidade > 1
        ? `calendarios_${sufixo}.zip`
        : `calendario_${sufixo}.${payload.formato.toLowerCase()}`
    return { blob: response.data, fileName: getFileNameFromDisposition(disposition, fallback) }
  },
  /** Só APROVADA, da aprovação mais recente para a mais antiga. */
  relatorios: async (filters: RelatorioFilters): Promise<Paginated<Visita>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<Visita>>>('/operacional/visitas/relatorios', {
        params: filters,
      }),
    ),
  getById: (id: string): Promise<VisitaDetalhe> => getDetalhe(`/operacional/visitas/${id}`),
  historico: async (id: string): Promise<VisitaHistorico[]> =>
    unwrap(
      await httpClient.get<ApiResponse<VisitaHistorico[]>>(`/operacional/visitas/${id}/historico`),
    ),
  create: (payload: VisitaCreatePayload): Promise<VisitaDetalhe> =>
    postDetalhe('/operacional/visitas', payload),
  remarcar: (id: string, dataPrevista: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/operacional/visitas/${id}/remarcacao`, { dataPrevista }),
  cancelar: (id: string, motivo: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/operacional/visitas/${id}/cancelamento`, { motivo }),
  /** Só APROVADA: volta para REALIZADA e `relatorioArquivoId` vem `null`. */
  reabrir: (id: string, motivo: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/operacional/visitas/${id}/reabertura`, { motivo }),
  /**
   * Qualquer status; a visita deixa de existir (GET por id → 404). Exige o token da biometria
   * (`useConfirmacaoBiometria`). 409 se outra pessoa a alterou ou sem aparelho cadastrado.
   */
  excluir: async (id: string, payload: VisitaExclusaoPayload): Promise<void> => {
    await httpClient.post(`/operacional/visitas/${id}/exclusao`, payload)
  },
}

/** Execução pelo responsável da equipe. A API só devolve visitas da equipe do usuário. */
export const minhasVisitasApi = {
  /** Sem `status`: AGENDADA, EM_ANDAMENTO, PENDENTE e DEVOLVIDA. */
  list: async (filters: MinhasVisitasFilters): Promise<Paginated<Visita>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<Visita>>>('/minhas-visitas', { params: filters }),
    ),
  getById: (id: string): Promise<VisitaDetalhe> => getDetalhe(`/minhas-visitas/${id}`),
  /** Check-in: as etapas passam a vir preenchidas. */
  iniciar: (id: string): Promise<VisitaDetalhe> => postDetalhe(`/minhas-visitas/${id}/inicio`),
  enviarFoto: async (
    id: string,
    etapaId: string,
    payload: FotoUploadPayload,
  ): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.post<ApiResponse<VisitaDetalhe>>(
        `/minhas-visitas/${id}/etapas/${etapaId}/fotos`,
        payload,
        { timeout: UPLOAD_TIMEOUT_MS },
      ),
    ),
  removerFoto: async (id: string, fotoId: string): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.delete<ApiResponse<VisitaDetalhe>>(`/minhas-visitas/${id}/fotos/${fotoId}`),
    ),
  atualizarEtapa: async (
    id: string,
    etapaId: string,
    payload: EtapaUpdatePayload,
  ): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.put<ApiResponse<VisitaDetalhe>>(
        `/minhas-visitas/${id}/etapas/${etapaId}`,
        payload,
      ),
    ),
  pausar: (id: string, motivo: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/minhas-visitas/${id}/pausa`, { motivo }),
  retomar: (id: string): Promise<VisitaDetalhe> => postDetalhe(`/minhas-visitas/${id}/retomada`),
  /** 400 com `errors.Etapas` quando falta foto ou "não se aplica". */
  finalizar: (id: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/minhas-visitas/${id}/finalizacao`),
}

/** Revisão do relatório (analista). */
export const revisaoApi = {
  /** Só visitas REALIZADA. */
  list: async (filters: RevisaoFilters): Promise<Paginated<Visita>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<Visita>>>('/operacional/revisao', {
        params: filters,
      }),
    ),
  /** Gera o PDF: `relatorioArquivoId` vem preenchido. */
  aprovar: (id: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/operacional/revisao/${id}/aprovacao`),
  devolver: (id: string, motivo: string): Promise<VisitaDetalhe> =>
    postDetalhe(`/operacional/revisao/${id}/devolucao`, { motivo }),
  enviarFoto: async (
    id: string,
    etapaId: string,
    payload: FotoUploadPayload,
  ): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.post<ApiResponse<VisitaDetalhe>>(
        `/operacional/revisao/${id}/etapas/${etapaId}/fotos`,
        payload,
        { timeout: UPLOAD_TIMEOUT_MS },
      ),
    ),
  removerFoto: async (id: string, fotoId: string): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.delete<ApiResponse<VisitaDetalhe>>(
        `/operacional/revisao/${id}/fotos/${fotoId}`,
      ),
    ),
  atualizarEtapa: async (
    id: string,
    etapaId: string,
    payload: EtapaUpdatePayload,
  ): Promise<VisitaDetalhe> =>
    unwrap(
      await httpClient.put<ApiResponse<VisitaDetalhe>>(
        `/operacional/revisao/${id}/etapas/${etapaId}`,
        payload,
      ),
    ),
}
